// Centralized question configuration for the TRAIL assessment.
// Each section has an ordered list of steps. Steps are rendered by type.

export const SECTIONS = [
  { key: "profile", label: "About You" },
  { key: "assets", label: "Assets" },
  { key: "income", label: "Income" },
  { key: "taxes", label: "Taxes" },
  { key: "protection", label: "Protection" },
  { key: "legacy", label: "Legacy" },
];

export const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky",
  "Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi",
  "Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico",
  "New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania",
  "Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
  "Virginia","Washington","West Virginia","Wisconsin","Wyoming","District of Columbia",
];

const investable = [
  { value: "under_25k", label: "Under $25,000" },
  { value: "25_99k", label: "$25,000 – $99,999" },
  { value: "100_249k", label: "$100,000 – $249,999" },
  { value: "250_499k", label: "$250,000 – $499,999" },
  { value: "500_999k", label: "$500,000 – $999,999" },
  { value: "1m_1.99m", label: "$1,000,000 – $1,999,999" },
  { value: "2m_4.99m", label: "$2,000,000 – $4,999,999" },
  { value: "5m_plus", label: "$5,000,000+" },
  { value: "unsure", label: "I'm not sure" },
];

const accountTypeOptions = [
  { value: "checking_savings", label: "Checking / savings / money market" },
  { value: "cds_treasury", label: "CDs / Treasury securities" },
  { value: "401k_403b_457", label: "401(k), 403(b), or 457 plan" },
  { value: "trad_ira", label: "Traditional IRA" },
  { value: "roth", label: "Roth IRA or Roth 401(k)" },
  { value: "brokerage", label: "Taxable brokerage account" },
  { value: "annuity", label: "Annuity" },
  { value: "pension", label: "Pension benefit" },
  { value: "employer_stock", label: "Employer / company stock" },
  { value: "rental_re", label: "Rental real estate" },
  { value: "business", label: "Business ownership" },
  { value: "life_cv", label: "Life insurance with cash value" },
  { value: "other", label: "Other" },
  { value: "unsure", label: "I'm not sure" },
];

// Steps: `type` drives the renderer.
// Types: number, radio, multi, partner, retirement_status, spending, income_sources_and_predictable, current_expenses
export const STEPS = [
  // -------- PROFILE --------
  { id: "age", section: "profile", type: "number", title: "How old are you?", min: 18, max: 100, suffix: "years", required: true },
  { id: "partner", section: "profile", type: "partner", title: "Are you planning for retirement with a spouse or partner?" },
  { id: "retirement_status", section: "profile", type: "retirement_status", title: "Where are you in your retirement journey?" },
  {
    id: "current_expenses", section: "profile", type: "current_expenses",
    title: "About how much does your household currently spend each month?",
    helper: "An estimate is fine. Include housing, food, transportation, utilities, insurance, debt payments, and normal everyday expenses.",
  },
  {
    id: "debt_situation", section: "profile", type: "radio",
    title: "Which best describes your current debt situation?",
    helper: "Think about credit cards, personal loans, student loans, auto loans, mortgages, and other outstanding debt.",
    options: [
      { value: "A", label: "I have little or no debt outside of manageable housing debt" },
      { value: "B", label: "I have debt, but payments are comfortable within my monthly budget" },
      { value: "C", label: "Debt payments take up a noticeable portion of my monthly income" },
      { value: "D", label: "Debt payments make it difficult to save or invest consistently" },
      { value: "E", label: "I'm not sure" },
    ],
  },
  {
    id: "liability_types", section: "profile", type: "multi",
    title: "Which types of debt or financial obligations do you currently have?",
    helper: "Select all that apply. This is used for context only — a reasonable mortgage or student loan does not reduce your score.",
    options: [
      { value: "mortgage", label: "Mortgage" },
      { value: "credit_card_revolving", label: "Credit card balance carried month to month" },
      { value: "student", label: "Student loans" },
      { value: "auto", label: "Auto loan" },
      { value: "personal", label: "Personal loan" },
      { value: "business_debt", label: "Business debt" },
      { value: "other_debt", label: "Other debt" },
      { value: "none", label: "None" },
      { value: "prefer_not", label: "Prefer not to say" },
    ],
  },

  // -------- ASSETS --------
  {
    id: "investable_assets", section: "assets", type: "radio",
    title: "Approximately how much do you currently have in savings and investments that could support retirement?",
    title_young: "Approximately how much do you currently have in savings and investments earmarked for long-term goals?",
    helper: "Include retirement accounts, brokerage accounts, savings, CDs, and annuities. You may exclude your primary residence.",
    helper_young: "Include money earmarked for emergency reserves, investing, retirement, and other long-term financial goals. You may exclude your primary residence.",
    options: investable,
  },
  {
    id: "account_types", section: "assets", type: "multi",
    title: "Which types of accounts or assets do you currently have?",
    helper: "Select all that apply.",
    options: accountTypeOptions,
  },
  {
    id: "asset_liquidity", section: "assets", type: "radio",
    title: "If an unexpected expense came up, about how many months of normal household expenses could you cover without borrowing, selling long-term investments at an unwanted time, or paying an early-withdrawal or surrender penalty?",
    options: [
      { value: "12+", label: "12 months or more" },
      { value: "6-11", label: "6 – 11 months" },
      { value: "3-5", label: "3 – 5 months" },
      { value: "1-2", label: "1 – 2 months" },
      { value: "<1", label: "Less than 1 month" },
      { value: "unsure", label: "I'm not sure" },
    ],
  },
  {
    id: "asset_diversification", section: "assets", type: "radio",
    title: "Is a large portion of your investment wealth concentrated in any one investment, company, sector, or property?",
    helper: "For example, a large employer-stock position or one investment representing roughly 20% or more of your investable assets.",
    options: [
      { value: "diversified", label: "No — my investments are reasonably diversified" },
      { value: "one_significant", label: "Yes — one area represents a significant portion" },
      { value: "several_high", label: "Yes — several areas are highly concentrated" },
      { value: "unsure", label: "I'm not sure" },
    ],
  },
  {
    id: "asset_risk", section: "assets", type: "radio",
    title: "How confident are you that the investment risk you're taking matches your retirement goals and the amount of risk you actually need?",
    title_young: "How confident are you that the investment risk you're taking matches your long-term goals and time horizon?",
    options: [
      { value: "very_confident", label: "Very confident — it has been reviewed as part of a retirement plan" },
      { value: "fairly_confident", label: "Fairly confident" },
      { value: "somewhat_unsure", label: "Somewhat unsure" },
      { value: "not_reviewed", label: "It has not been reviewed recently" },
      { value: "dont_know", label: "I don't know how much risk I'm taking" },
    ],
  },
  {
    id: "asset_location", section: "assets", type: "radio",
    conditional: "notYoung",
    title: "Do you know approximately how your investments are divided between taxable, tax-deferred, and tax-free accounts?",
    helper: "Taxable (brokerage, bank interest), tax-deferred (traditional IRA, 401(k), 403(b)), tax-free (Roth IRA, Roth 401(k)).",
    options: [
      { value: "know", label: "Yes — I know the approximate breakdown" },
      { value: "general", label: "I have a general idea" },
      { value: "not_really", label: "Not really" },
      { value: "dont_know", label: "I don't know" },
    ],
  },
  {
    id: "asset_review", section: "assets", type: "radio",
    title: "When was your overall investment strategy last reviewed in the context of your retirement plan?",
    title_young: "When was your overall investment strategy last reviewed in the context of your long-term goals?",
    options: [
      { value: "<1yr", label: "Within the last year" },
      { value: "1-2yr", label: "1 – 2 years ago" },
      { value: "3-5yr", label: "3 – 5 years ago" },
      { value: ">5yr", label: "More than 5 years ago" },
      { value: "never", label: "It has never been reviewed as part of a retirement plan" },
      { value: "unsure", label: "I'm not sure" },
    ],
  },

  // -------- INCOME --------
  { id: "spending", section: "income", type: "spending",
    conditional: "notYoung",
    title: "About how much do you expect your household to spend each month in retirement?",
    helper: "An estimate is fine. Think about housing, food, transportation, healthcare, travel, and everyday spending." },
  { id: "income_sources_predictable", section: "income", type: "income_sources_and_predictable",
    conditional: "notYoung",
    title: "Which sources do you expect to provide regular income in retirement?" },
  {
    id: "future_savings", section: "income", type: "radio",
    conditional: "young",
    title: "Are you consistently setting aside money toward long-term goals?",
    options: [
      { value: "consistent", label: "Yes — I save or invest consistently and have a defined approach" },
      { value: "no_target", label: "Yes — I save regularly, but without a specific long-term target" },
      { value: "when_cash", label: "I save or invest when cash flow allows" },
      { value: "little", label: "I currently save very little toward long-term goals" },
      { value: "unsure", label: "I'm not sure" },
    ],
  },
  {
    id: "young_account_strategy", section: "income", type: "radio",
    conditional: "young",
    title: "Do you understand how your savings and investment accounts are intended to support your different long-term goals?",
    options: [
      { value: "clear", label: "Yes — I have a clear strategy for different accounts and goals" },
      { value: "generally", label: "Generally, yes" },
      { value: "thought", label: "I've thought about it but don't have a clear strategy" },
      { value: "no", label: "No" },
      { value: "unsure", label: "I'm not sure" },
    ],
  },
  {
    id: "income_withdrawal", section: "income", type: "radio",
    conditional: "notYoung",
    title: "Have you mapped out which accounts you'll draw from during retirement and in what order?",
    options: [
      { value: "coordinated", label: "Yes — we have a coordinated withdrawal strategy" },
      { value: "generally", label: "Generally, but not in much detail" },
      { value: "thought", label: "I've thought about it but do not have a strategy" },
      { value: "no", label: "No" },
      { value: "not_familiar", label: "I'm not familiar with withdrawal sequencing" },
    ],
  },
  {
    id: "income_longevity", section: "income", type: "radio",
    title: "Has your retirement plan been projected through your expected lifetime?",
    title_young: "Have you estimated whether your current savings and investment strategy is on track for your long-term financial goals?",
    options: [
      { value: "90-95", label: "Yes — through at least age 90–95 or beyond" },
      { value: "unsure_assumptions", label: "Yes, but I'm not sure what assumptions were used" },
      { value: "rough", label: "I only have a rough estimate" },
      { value: "no", label: "No" },
      { value: "unsure", label: "I'm not sure" },
    ],
  },
  {
    id: "income_stress", section: "income", type: "radio",
    conditional: "notYoung",
    title: "Has your retirement strategy been tested against difficult scenarios?",
    helper: "Examples may include a major market decline, higher inflation, living longer than expected, unexpected healthcare costs, or one spouse passing away first.",
    options: [
      { value: "several", label: "Yes — several scenarios have been tested" },
      { value: "some", label: "Some scenarios have been reviewed" },
      { value: "briefly", label: "Only briefly" },
      { value: "no", label: "No" },
      { value: "unsure", label: "I'm not sure" },
    ],
  },
  {
    id: "young_stress_inflation", section: "income", type: "radio",
    conditional: "young",
    title: "Have you considered how rising costs, market declines, income interruptions, or major unexpected expenses could affect your long-term plan?",
    options: [
      { value: "several", label: "Yes — several scenarios are considered in my planning" },
      { value: "some", label: "Some scenarios are considered" },
      { value: "briefly", label: "Only briefly" },
      { value: "no", label: "No" },
      { value: "unsure", label: "I'm not sure" },
    ],
  },

  // -------- TAX --------
  {
    id: "tax_strategy", section: "taxes", type: "radio",
    title: "Do you have a strategy for managing taxes throughout retirement, not just for the current tax year?",
    title_young: "Do you have a strategy for managing taxes across your saving and investing decisions, not just for the current tax year?",
    options: [
      { value: "reviewed", label: "Yes — and it is reviewed regularly" },
      { value: "discussed", label: "We have discussed one, but it is not fully coordinated" },
      { value: "this_year", label: "I mostly focus on this year's taxes" },
      { value: "no", label: "No" },
      { value: "unsure", label: "I'm not sure" },
    ],
  },
  {
    id: "tax_rmd", section: "taxes", type: "radio", conditional: "hasTaxDeferredNotYoung",
    title: "If you have tax-deferred retirement accounts, have future Required Minimum Distributions (RMDs) been considered or projected?",
    options: [
      { value: "projected", label: "Yes — future RMDs have been projected" },
      { value: "discussed", label: "They have been discussed but not fully projected" },
      { value: "know_not_analyzed", label: "I know about RMDs but have not analyzed them" },
      { value: "no", label: "No" },
      { value: "unsure", label: "I'm not sure what RMDs are" },
    ],
  },
  {
    id: "tax_roth", section: "taxes", type: "radio",
    title: "Have Roth conversions been evaluated as part of your retirement tax planning?",
    title_young: "Have you considered how Roth, pre-tax, and taxable accounts may work together over time?",
    options: [
      { value: "evaluated", label: "Yes — timing and potential amounts have been evaluated" },
      { value: "generally", label: "Yes — but only generally" },
      { value: "heard", label: "I've heard about Roth conversions but haven't evaluated them" },
      { value: "no", label: "No" },
      { value: "unsure", label: "I'm not sure what this means" },
    ],
  },

  // -------- PROTECTION --------
  {
    id: "insurance_ltc", section: "protection", type: "radio",
    conditional: "notYoung",
    title: "If you or your spouse or partner needed extended care later in life, how do you currently expect those costs would be handled?",
    options: [
      { value: "dedicated", label: "We have dedicated coverage and/or specifically identified assets for this risk" },
      { value: "combination", label: "We expect to use a combination of insurance and personal assets" },
      { value: "general", label: "We would primarily use general retirement savings" },
      { value: "family", label: "We would primarily rely on family" },
      { value: "none", label: "We have not planned for this" },
      { value: "unsure", label: "I'm not sure" },
    ],
  },
  {
    id: "insurance_emergency", section: "protection", type: "radio",
    conditional: "young",
    title: "If your income stopped unexpectedly because of illness, disability, or another major life event, how prepared would your household be?",
    options: [
      { value: "defined", label: "We have a defined emergency/protection strategy" },
      { value: "some", label: "We have some protections and reserves in place" },
      { value: "short_period", label: "We would manage for a short period but it would become difficult" },
      { value: "immediate_problem", label: "It would create an immediate financial problem" },
      { value: "unsure", label: "I'm not sure" },
    ],
  },
  {
    id: "insurance_survivor", section: "protection", type: "radio",
    title: "If one of you passed away first, has the surviving household's income and spending been reviewed?",
    conditional: "partnered",
    options: [
      { value: "modeled", label: "Yes — survivor income and spending have been specifically modeled" },
      { value: "generally", label: "Generally, but not in detail" },
      { value: "briefly", label: "We have discussed it briefly" },
      { value: "no", label: "No" },
      { value: "unsure", label: "I'm not sure" },
    ],
  },
  {
    id: "insurance_survivor_single", section: "protection", type: "radio",
    title: "Have you planned for how your finances would be handled if you became unable to manage them yourself?",
    conditional: "single",
    // Stored into insurance_survivor for scoring parity
    storeAs: "insurance_survivor",
    options: [
      { value: "modeled", label: "Yes — appropriate arrangements have been reviewed" },
      { value: "generally", label: "Some arrangements are in place" },
      { value: "briefly", label: "Only partially" },
      { value: "no", label: "No" },
      { value: "unsure", label: "I'm not sure" },
    ],
  },
  {
    id: "insurance_review", section: "protection", type: "radio",
    title: "When were your major insurance and healthcare-related protections last reviewed in the context of your retirement plan?",
    title_young: "When were your major insurance and healthcare-related protections last reviewed in the context of your overall financial plan?",
    helper: "This might include life insurance, long-term care planning, Medicare or health coverage, or other relevant protection needs.",
    helper_young: "This might include life, disability, health, or other relevant protection needs.",
    options: [
      { value: "<3yr", label: "Within the last 3 years" },
      { value: "some", label: "Some items have been reviewed" },
      { value: ">3yr", label: "More than 3 years ago" },
      { value: "never", label: "Never" },
      { value: "unsure", label: "I'm not sure" },
    ],
  },

  // -------- LEGACY --------
  {
    id: "estate_documents", section: "legacy", type: "multi",
    title: "Which of the following planning documents do you currently have?",
    helper: "Select all that apply.",
    options: [
      { value: "will", label: "Will" },
      { value: "fin_poa", label: "Financial power of attorney" },
      { value: "health_poa", label: "Healthcare power of attorney / advance directive" },
      { value: "trust", label: "Trust" },
      { value: "none", label: "None" },
      { value: "unsure", label: "I'm not sure" },
    ],
  },
  {
    id: "legacy_beneficiary", section: "legacy", type: "radio",
    title: "When were the beneficiary designations on your retirement accounts, insurance policies, and other relevant accounts last reviewed?",
    options: [
      { value: "<1yr", label: "Within the last year" },
      { value: "2-3yr", label: "Within the last 2 – 3 years" },
      { value: ">3yr", label: "More than 3 years ago" },
      { value: "never", label: "Never" },
      { value: "unsure", label: "I'm not sure" },
    ],
  },
  {
    id: "legacy_goal", section: "legacy", type: "radio",
    title: "Do you have a clear plan for what you would like to happen to your remaining assets?",
    options: [
      { value: "reflected", label: "Yes — our financial/estate planning reflects those goals" },
      { value: "clear_general", label: "Yes — I have clear general goals" },
      { value: "thought", label: "I've thought about it but nothing is formalized" },
      { value: "no", label: "No" },
      { value: "unsure", label: "I'm not sure" },
    ],
  },
];

export function stepIsVisible(step, answers) {
  if (!step.conditional) return true;
  const accounts = answers.account_types || [];
  if (step.conditional === "hasTaxDeferred") {
    return accounts.includes("401k_403b_457") || accounts.includes("trad_ira");
  }
  if (step.conditional === "hasTaxDeferredNotYoung") {
    const hasIt = accounts.includes("401k_403b_457") || accounts.includes("trad_ira");
    return hasIt && answers.retirement_status !== "10plus";
  }
  if (step.conditional === "partnered") return answers.partner === "yes";
  if (step.conditional === "single") return answers.partner === "no";
  if (step.conditional === "young") return answers.retirement_status === "10plus";
  if (step.conditional === "notYoung") return !!answers.retirement_status && answers.retirement_status !== "10plus";
  return true;
}

export function visibleSteps(answers) {
  return STEPS.filter((s) => stepIsVisible(s, answers));
}

// Life stage helpers
export function isYoungStage(answers) {
  // "More than 10 years from retirement" → younger wealth-building framing
  return answers?.retirement_status === "10plus";
}

export function stepTitle(step, answers) {
  return isYoungStage(answers) && step.title_young ? step.title_young : step.title;
}

export function stepHelper(step, answers) {
  return isYoungStage(answers) && step.helper_young ? step.helper_young : step.helper;
}
